import { useState, useEffect, useRef, useCallback } from "react";
import {
  GetBookingActivityAction,
  GetPushConfigAction,
  SavePushSubscriptionAction,
  getUserInfoFromToken,
} from "../store/action";
import { UserRole } from "../constants/roles";
import { ACCESS_TOKEN } from "../constants/commonConst";
import {
  isPushSupported,
  registerServiceWorker,
  showLocalNotification,
  subscribeDevice,
} from "../utils/pushSubscription";

/**
 * Tells staff when a customer books or cancels.
 *
 * **Web Push first.** Once a staff member allows notifications, this device is
 * subscribed (utils/pushSubscription.js) and the server sends each alert
 * itself, from the notification outbox, the moment the booking commits. The
 * operating system delivers it to the service worker, so it reaches a phone
 * whose app and browser are both closed — the case that matters, and the one
 * the in-tab design before it could never cover.
 *
 * **Polling as the fallback.** A browser without push, or a server with no
 * VAPID keys, gets what there was before: this tab asks every 45 seconds while
 * the dashboard is open, and shows what it hears. Never both at once — a
 * device on push would otherwise see every booking twice.
 */

/** Slow enough to be invisible in server load, fast enough to feel immediate. */
const POLL_INTERVAL_MS = 45000;

/**
 * Browsers treat an unsolicited permission prompt as a dark pattern, and users
 * reflexively deny one they did not ask for — a denial is close to permanent,
 * since almost nobody reverses it in site settings. So the prompt only ever
 * follows a click.
 */
export const NOTIFICATION_SUPPORTED =
  typeof window !== "undefined" && "Notification" in window;

const PUSH_SUPPORTED = isPushSupported();

const permissionNow = () =>
  NOTIFICATION_SUPPORTED ? Notification.permission : "unsupported";

const currentToken = () => {
  try {
    return localStorage.getItem(ACCESS_TOKEN);
  } catch {
    return null;
  }
};

/**
 * The server's public key, fetched once per page and shared. A promise, so the
 * click that subscribes can await one already on its way instead of starting
 * a round trip inside the gesture.
 */
let pushKey = null;

const loadPushKey = () => {
  if (!pushKey) {
    pushKey = GetPushConfigAction()
      .then((config) => config?.publicKey ?? null)
      .catch(() => {
        pushKey = null;
        return null;
      });
  }
  return pushKey;
};

/**
 * Subscribes this device and registers it with the server, once per session:
 * remembered against the token, so navigating the dashboard does not repeat
 * it, while signing in as someone else does. Resolves to whether push is now
 * how alerts reach this device.
 */
let enabled = { token: null, result: null };

const enablePush = () => {
  const token = currentToken();
  if (enabled.result && enabled.token === token) return enabled.result;

  const result = (async () => {
    try {
      const publicKey = await loadPushKey();
      if (!publicKey) return false;

      const subscription = await subscribeDevice(publicKey);
      if (!subscription.endpoint || !subscription.p256dh || !subscription.auth) {
        return false;
      }

      await SavePushSubscriptionAction(subscription);
      return true;
    } catch {
      return false;
    }
  })();

  enabled = { token, result };

  // A failure is not remembered: the next dashboard load tries again.
  result.then((ok) => {
    if (!ok && enabled.result === result) enabled = { token: null, result: null };
  });

  return result;
};

export const useStaffNotifications = () => {
  const info = getUserInfoFromToken();
  const role = info?.role;
  // A SystemOwner simulating a business holds its owner's token, but the
  // business's booking alerts are the owner's, not theirs: no prompt, no
  // subscription, no polling in platform staff's browser.
  const isStaff =
    (role === UserRole.COMPANY_ADMIN || role === UserRole.COMPANY_EMPLOYEE) &&
    info?.impersonatorId == null;

  const [permission, setPermission] = useState(permissionNow);

  // How alerts reach this device once permission is granted: "push" (the
  // server sends them, app open or not) or "poll" (this tab asks). Null while
  // that is still being worked out.
  const [delivery, setDelivery] = useState(null);

  // The polling cursor. A ref rather than state on purpose: it changes on every
  // poll, and re-rendering the whole dashboard 80 times an hour to store a
  // timestamp nothing displays would be pure waste.
  const cursor = useRef(null);

  // Ready before the click: the worker installed and the key in hand, so the
  // subscription that follows the tap happens while Safari still counts it as
  // the tap's.
  useEffect(() => {
    if (!isStaff || !PUSH_SUPPORTED || permission === "denied") return;
    loadPushKey();
    registerServiceWorker().catch(() => {});
  }, [isStaff, permission]);

  const request = useCallback(async () => {
    if (!NOTIFICATION_SUPPORTED) return "unsupported";

    const result = await Notification.requestPermission();

    // Subscribed right here, still inside the chain the tap started, rather
    // than in an effect after the re-render.
    if (result === "granted" && isStaff && PUSH_SUPPORTED) await enablePush();

    setPermission(result);
    return result;
  }, [isStaff]);

  // Every dashboard load with permission already granted re-confirms the
  // subscription: that is how a device subscribed before a password change, a
  // key rotation or a sign-out comes back without anyone asking twice.
  useEffect(() => {
    if (!isStaff || permission !== "granted") return undefined;

    if (!PUSH_SUPPORTED) {
      setDelivery("poll");
      return undefined;
    }

    let cancelled = false;
    enablePush().then((ok) => {
      if (!cancelled) setDelivery(ok ? "push" : "poll");
    });

    return () => {
      cancelled = true;
    };
  }, [isStaff, permission]);

  useEffect(() => {
    if (!isStaff || permission !== "granted" || delivery !== "poll") return undefined;

    let cancelled = false;
    let timer = null;

    const show = (event) => {
      const when = new Date(event.bookingDateTime);

      // Parsed and read back as UTC deliberately. BookingDateTime is the
      // business's wall-clock time carrying a `Z`; letting the browser convert
      // it to its own zone would show a staff member travelling abroad an hour
      // their salon never offered.
      const time = when.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      });

      const title =
        event.kind === "Cancelled" || event.kind === 1
          ? `${event.customerName} cancelled`
          : `New booking — ${event.customerName}`;

      // `tag` collapses repeats: a poll that somehow reports the same booking
      // twice replaces its own popup instead of stacking a second one.
      showLocalNotification(title, {
        body: `${event.serviceName} · ${event.resourceName}\n${time}`,
        tag: `booking-${event.bookingId}-${event.kind}`,
      });
    };

    const poll = async () => {
      try {
        const data = await GetBookingActivityAction(cursor.current ?? undefined);
        if (cancelled) return;

        // Only after the first response is the cursor real. Until then the
        // server has been told "no since", and answers with a cursor and no
        // events — which is what stops a freshly opened dashboard replaying the
        // morning.
        if (cursor.current) (data.events ?? []).forEach(show);
        cursor.current = data.serverTimeUtc;
      } catch {
        // A failed poll is not worth surfacing: the next one is 45 seconds away,
        // and an expired session is already handled by the axios interceptor.
        // Keeping the old cursor means nothing is skipped when it recovers.
      } finally {
        if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isStaff, permission, delivery]);

  return {
    /** Whether to offer the prompt at all. */
    canAsk: isStaff && NOTIFICATION_SUPPORTED && permission === "default",
    permission,
    delivery,
    request,
  };
};

export default useStaffNotifications;
